import { PureComponent } from "react";
import { connect } from "react-redux";
import Select from "react-select";

import { HStack } from "widgets";

import { Addon } from "@/ui-modules/core/redux-form/Addon.js";
import { getCompatibleRefGenome } from "@/app-utils/variant.ts";
import type { Organism } from "@/graphql.ts";
import { updateReferenceGenome } from "@/redux/modules/auth.ts";
import type { IAppState } from "@/redux/reducer.ts";

interface IRefGenomeSelectorOwnProps {
  referenceGenomes?: Organism[];
}

interface IRefGenomeSelectorAppStateProps {
  preferredRefGenomeId: number | undefined | null;
}

interface IRefGenomeSelectorDispatchProps {
  updateReferenceGenome: (refGenomeId: number) => void;
}

export interface IRefGenomeSelectorProps
  extends IRefGenomeSelectorOwnProps,
    IRefGenomeSelectorAppStateProps,
    IRefGenomeSelectorDispatchProps {}

function mapStateToProps(state: IAppState, _ownProps: IRefGenomeSelectorProps): IRefGenomeSelectorAppStateProps {
  return {
    preferredRefGenomeId: state.auth.session && state.auth.session.params?.referenceGenome,
  };
}

function mapDispatchToProps(dispatch): IRefGenomeSelectorDispatchProps {
  return {
    updateReferenceGenome: (refGenomeId: number) => dispatch(updateReferenceGenome(refGenomeId)),
  };
}

class RefGenomeSelector extends PureComponent<IRefGenomeSelectorProps> {
  determineRefGenomeId() {
    if (!this.props.referenceGenomes) {
      // in case e.g. the loading of the entity is pending
      return;
    }

    // we define which ref genome matches best
    const refGenomeId = getCompatibleRefGenome(this.props.preferredRefGenomeId, this.props.referenceGenomes);
    if (refGenomeId && refGenomeId !== this.props.preferredRefGenomeId) {
      this.props.updateReferenceGenome(refGenomeId);
    }
  }

  componentDidMount() {
    this.determineRefGenomeId();
  }

  componentDidUpdate(prevProps: Readonly<IRefGenomeSelectorProps>) {
    if (this.props.referenceGenomes !== prevProps.referenceGenomes) {
      this.determineRefGenomeId();
    }
    if (this.props.preferredRefGenomeId !== prevProps.preferredRefGenomeId) {
      this.determineRefGenomeId();
    }
  }

  onChange = (organism: Organism) => {
    this.props.updateReferenceGenome(organism.id);
  };

  render() {
    const selectedReferenceGenome =
      this.props.referenceGenomes &&
      this.props.referenceGenomes.find((o: Organism) => o.id === this.props.preferredRefGenomeId);

    return (
      <HStack>
        <Addon label={"Ref genome"} />
        <div style={{ width: 300 }}>
          <Select
            name="filterByReferenceGenome"
            placeholder={"select reference genome"}
            className={"react-select"}
            classNamePrefix={"react-select"}
            isDisabled={false}
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({
                ...base,
                zIndex: 650,
              }),
            }}
            isClearable={false}
            isSearchable={true}
            options={this.props.referenceGenomes || []}
            getOptionLabel={(option: Organism) => option.name!}
            getOptionValue={(option: Organism) => String(option.id)!} // ref genome exists so we always have a valid id
            onChange={this.onChange}
            value={selectedReferenceGenome}
            noOptionsMessage={() => "No matching genome..."}
          />
        </div>
      </HStack>
    );
  }
}

export default connect<IRefGenomeSelectorAppStateProps, IRefGenomeSelectorDispatchProps, IRefGenomeSelectorOwnProps>(
  mapStateToProps,
  mapDispatchToProps,
)(RefGenomeSelector);
